import { Asset } from "@/schemas";
import { AssetLogo } from "@/components/asset/asset-logo";
import { getExchangeLabel } from "@/lib/exchange-mapping";

function getAssetDetails(asset: Asset): { main: string; details: string } {
  switch (asset.type) {
    case "stock": {
      const stockExchange = getExchangeLabel(asset.exchange);
      return {
        main: asset.name,
        details: stockExchange
          ? `${asset.symbol} • ${stockExchange}`
          : asset.symbol,
      };
    }
    case "etf": {
      const etfExchange = getExchangeLabel(asset.exchange);
      return {
        main: asset.name,
        details: etfExchange
          ? `${asset.symbol} • ${etfExchange}`
          : asset.symbol,
      };
    }
    case "fund":
      return {
        main: asset.name,
        details: asset.fund_family
          ? `${asset.symbol} • ${asset.fund_family}`
          : asset.symbol,
      };
    case "crypto":
      return {
        main: asset.name,
        details: asset.symbol,
      };
    case "fiat":
      return {
        main: asset.name,
        details: asset.symbol,
      };
    case "index":
      return {
        main: asset.name,
        details: asset.provider
          ? `${asset.symbol} • ${asset.provider}`
          : asset.symbol,
      };
    default:
      return {
        main: (asset as { name: string }).name,
        details: (asset as { symbol: string }).symbol,
      };
  }
}

interface AssetDisplayProps {
  asset: Asset;
  size?: "default" | "sm";
  showLogo?: boolean;
  hideName?: boolean; // Show only symbol + logo (compact mode)
}

export function AssetDisplay({ 
  asset, 
  size = "default", 
  showLogo = true,
  hideName = false,
}: AssetDisplayProps) {
  if (!asset) {
    return <span className="text-muted-foreground italic">No asset</span>;
  }

  const { main, details } = getAssetDetails(asset);
  const logoSize = size === "sm" ? "sm" : "md";

  // Compact mode: just logo + symbol
  if (hideName) {
    return (
      <div className="flex items-center gap-1.5">
        {showLogo && <AssetLogo asset={asset} size="sm" />}
        <span className="font-medium text-sm">{asset.symbol}</span>
      </div>
    );
  }

  if (size === "sm") {
    return (
      <div className="flex items-center gap-2">
        {showLogo && <AssetLogo asset={asset} size={logoSize} />}
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium truncate">{main}</div>
          <div className="text-xs text-muted-foreground truncate">{details}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3">
      {showLogo && <AssetLogo asset={asset} size={logoSize} />}
      <div className="min-w-0">
        <div className="font-medium truncate">{main}</div>
        <div className="text-sm text-stone-400 truncate">{details}</div>
      </div>
    </div>
  );
}
